// services/conversationService.js
//
// Client side of 1:1 direct messages (conversations/dm_<minUid>_<maxUid>).
// The FIRST message is always sent through the `sendMessageRequest` callable
// (it creates the thread + enforces block/privacy); once a thread is ACTIVE,
// messages are plain client writes allowed by firestore.rules
// ("Messaging foundation"). Returns { data, error } and never throws.
import {
  collection,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { ref as storageRef, deleteObject } from "firebase/storage";
import { auth, db, functions, storage, COLLECTIONS } from "../config/firebase";
import { ClubService } from "./clubService";
import { UploadService } from "./uploadService";
import { ConnectionService } from "./connectionService";
import { addMessageDocWithReplyFallback } from "./chatWriteHelpers";

const sendMessageRequestCallable = httpsCallable(functions, "sendMessageRequest");


function errorCodeOf(error) {
  const code = (error && error.code) || "";
  return String(code).replace(/^functions\//, "");
}

function friendlySendError(error) {
  const code = errorCodeOf(error);
  if (code === "permission-denied") return "You can't message this person.";
  if (code === "failed-precondition") {
    return "This person hasn't accepted your message request.";
  }
  return "Couldn't send. Please try again.";
}

const MAX_REACTION_EMOJI_LENGTH = 32;
const NOT_ACTIVE_ERROR = "You can send this once your request is accepted.";

// reply_to snapshot shared by every send path (same shape as sendText).
function replyToPayload(replyTo) {
  if (!replyTo || !replyTo.message_id) return {};
  return {
    reply_to: {
      message_id: replyTo.message_id,
      snippet: String(replyTo.snippet || "").slice(0, 200),
      sender_name: replyTo.sender_name || "Someone",
    },
  };
}

function toMillis(value) {
  if (!value) return 0;
  if (value.toDate) return value.toDate().getTime();
  return new Date(value).getTime() || 0;
}

export class ConversationService {
  static dmIdFor(uidA, uidB) {
    const [a, b] = [uidA, uidB].sort();
    return `dm_${a}_${b}`;
  }

  // Live list of the user's DM threads, newest first. `onError` (optional):
  // called instead of onChange([]) on a listener error, for callers that must
  // not mistake a failure for "no threads" (the unread sweep).
  static subscribeMyConversations(uid, onChange, onError) {
    if (!uid) {
      onChange([]);
      return () => {};
    }
    const q = query(
      collection(db, COLLECTIONS.CONVERSATIONS),
      where("participant_uids", "array-contains", uid),
      // DMs only, filtered server-side so club-inbox threads never use up the
      // limit (TF232; index conversations(participant_uids, type, updated_at)).
      where("type", "==", "dm"),
      orderBy("updated_at", "desc"),
      limit(100)
    );
    return onSnapshot(
      q,
      (snapshot) => {
        const list = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data({ serverTimestamps: "estimate" }) }))
          .map((c) => ({
            ...c,
            otherUid: (c.participant_uids || []).find((u) => u !== uid) || null,
          }));
        onChange(list);
      },
      (error) => {
        if (__DEV__) console.error("subscribeMyConversations error:", error);
        if (onError) onError(error);
        else onChange([]);
      }
    );
  }

  static async getConversation(id) {
    try {
      const snap = await getDoc(doc(db, COLLECTIONS.CONVERSATIONS, id));
      return {
        data: snap.exists() ? { id: snap.id, ...snap.data() } : null,
        error: null,
      };
    } catch (error) {
      if (__DEV__) console.error("getConversation error:", error);
      return { data: null, error };
    }
  }

  static async sendRequest(recipientUid, text, clientId) {
    try {
      const result = await sendMessageRequestCallable({
        recipientUid,
        text,
        ...(clientId ? { clientId } : {}),
      });
      const d = (result && result.data) || {};
      return {
        data: {
          conversationId: d.conversationId,
          status: d.status,
          created: !!d.created,
          pendingFrom: d.pendingFrom || null,
        },
        error: null,
      };
    } catch (error) {
      if (__DEV__) console.error("sendRequest error:", error);
      return { data: null, error: friendlySendError(error) };
    }
  }

  static async _setStatus(id, status) {
    try {
      await updateDoc(doc(db, COLLECTIONS.CONVERSATIONS, id), {
        status,
        updated_at: serverTimestamp(),
      });
      return { data: { id, status }, error: null };
    } catch (error) {
      if (__DEV__) console.error("conversation status error:", error);
      return { data: null, error };
    }
  }

  // Id of a PENDING connection between me and the other DM participant where
  // I am the recipient and the thread is that connection's note, else null. While one exists, accept/decline must go
  // through respondToConnection so the connection and the DM move together
  // (the server activates / declines the thread). A failed read returns undefined.
  // The other participant of `dm_<a>_<b>` (null if it isn't a DM of mine).
  static otherUidFromDmId(dmId, me) {
    const parts = typeof dmId === "string" ? dmId.split("_") : [];
    if (parts.length !== 3 || parts[0] !== "dm" || !me || !parts.includes(me)) return null;
    return parts[1] === me ? parts[2] : parts[1];
  }

  static async _incomingPendingConnectionId(dmId) {
    try {
      const me = auth.currentUser?.uid;
      const other = ConversationService.otherUidFromDmId(dmId, me);
      if (!other) return null;
      // Only the thread a connection note opened answers with its connection;
      // an independent DM request is answered on its own.
      const convSnap = await getDoc(doc(db, COLLECTIONS.CONVERSATIONS, dmId));
      if (!convSnap.exists() || (convSnap.data() || {}).connection_note !== true) return null;
      const pairId = ConnectionService.pairId(me, other);
      const snap = await getDoc(doc(db, COLLECTIONS.CONNECTIONS, pairId));
      const d = snap.exists() ? snap.data() || {} : null;
      return d && d.status === "pending" && d.requester_uid !== me ? pairId : null;
    } catch (error) {
      // Unknown: answering the DM alone could split it from a pending connection.
      if (__DEV__) console.error("connection lookup error:", error);
      return undefined;
    }
  }

  static async _respond(id, accept) {
    const connectionId = await ConversationService._incomingPendingConnectionId(id);
    if (connectionId === undefined) {
      return { data: null, error: "Couldn't load this request. Check your connection and try again." };
    }
    if (!connectionId) {
      const target = accept ? "active" : "declined";
      const res = await ConversationService._setStatus(id, target);
      if (!res.error) return res;
      // Answered meanwhile (another screen, or its connection): that is success.
      const { data } = await ConversationService.getConversation(id);
      return data?.status === target ? { data: { id, status: target }, error: null } : res;
    }
    const { error } = await ConnectionService.respond(connectionId, accept);
    if (error) return { data: null, error };
    return { data: { id, status: accept ? "active" : "declined" }, error: null };
  }

  static accept(id) {
    return ConversationService._respond(id, true);
  }

  static decline(id) {
    return ConversationService._respond(id, false);
  }

  static async markRead(id) {
    try {
      const uid = auth.currentUser?.uid;
      if (!uid) return { data: null, error: "You must be signed in." };
      await updateDoc(doc(db, COLLECTIONS.CONVERSATIONS, id), {
        [`last_read_at.${uid}`]: serverTimestamp(),
      });
      return { data: { id }, error: null };
    } catch (error) {
      if (__DEV__) console.error("markRead error:", error);
      return { data: null, error };
    }
  }

  // uid -> { id, full_name, avatar_url } (missing profiles are absent).
  static async getProfiles(uids) {
    const out = new Map();
    try {
      const raw = await ClubService._fetchUsersByIds(uids || []);
      for (const [uid, data] of raw) {
        out.set(uid, {
          id: uid,
          full_name: data.full_name,
          avatar_url: data.avatar_url,
        });
      }
    } catch (error) {
      if (__DEV__) console.error("getProfiles error:", error);
    }
    return out;
  }

  // ChatRoom serviceApi (contract: hooks/useChatMessages.js). `getStatus`
  // returns the thread status ('request'|'active'|'declined') or null when no
  // thread exists yet.
  // `isConnected` (optional): true for an accepted connection — a pending or
  // declined thread then sends through the callable, which opens it.
  static createServiceApi({ conversationId, otherUid, getStatus, isConnected }) {
    const messagesRef = () =>
      collection(
        db,
        COLLECTIONS.CONVERSATIONS,
        conversationId,
        COLLECTIONS.CONVERSATION_MESSAGES
      );

    const listen = (onMessages, onError) => {
      const q = query(messagesRef(), orderBy("created_at", "desc"), limit(50));
      const profiles = new Map();
      // Snapshot callbacks await a profile fetch; a slower older one must not
      // overwrite a newer message list.
      let latestSeq = 0;
      return onSnapshot(
        q,
        async (snapshot) => {
          const seq = ++latestSeq;
          try {
            const docs = snapshot.docs.map((d) => ({
              id: d.id,
              ...d.data({ serverTimestamps: "estimate" }),
            }));
            const missing = [
              ...new Set(
                docs
                  .map((m) => m.user_id)
                  .filter((u) => u && !profiles.has(u))
              ),
            ];
            if (missing.length > 0) {
              const fetched = await ConversationService.getProfiles(missing);
              for (const uid of missing) {
                if (fetched.has(uid)) profiles.set(uid, fetched.get(uid));
              }
            }
            const messages = docs
              .map((m) => ({ ...m, user: profiles.get(m.user_id) || null }))
              .sort((a, b) => toMillis(a.created_at) - toMillis(b.created_at));
            if (seq !== latestSeq) return;
            onMessages(messages);
          } catch (error) {
            if (__DEV__) console.error("DM message subscription error:", error);
            onError?.(error);
          }
        },
        (error) => {
          if (__DEV__) console.error("DM message listener error:", error);
          onError?.(error);
        }
      );
    };

    // One write path for every non-text message; user_id/timestamps are set
    // here so callers only supply the type-specific fields.
    const addMessage = async (fields) => {
      try {
        // Reply fallback: rules match reply_to.sender_name against the live
        // author name; a rename since the profile was cached retries as
        // "Someone" (same as club / sub-chat sends).
        await addMessageDocWithReplyFallback(messagesRef(), {
          user_id: auth.currentUser?.uid,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
          ...fields,
        });
        return { error: null };
      } catch (error) {
        if (__DEV__) console.error("DM send error:", error);
        return { error };
      }
    };

    return {
      // Messages of a thread that doesn't exist yet can't be read (rules), so
      // the list stays empty; DirectMessageScreen remounts ChatRoom (new key)
      // once the thread appears, which re-runs this subscribe.
      subscribe: (cId, cType, onMessages, onError) => {
        if (!getStatus()) {
          onMessages([]);
          return () => {};
        }
        return listen(onMessages, onError);
      },
      sendText: async ({ text, clientId, replyTo }) => {
        let status = getStatus();
        // Connection still unknown (null): the server decides, never a local refusal.
        const connected = isConnected ? isConnected() !== false : false;
        if (!status || status === "declined" || (status === "request" && connected)) {
          const { data, error } = await ConversationService.sendRequest(
            otherUid,
            text,
            clientId
          );
          if (error) return { error };
          // The callable writes the text only when it (re)creates the
          // request. A thread that turned out to exist already (status not
          // loaded yet on a fast send) must not swallow the message.
          if (data?.created) return { error: null };
          status = data?.status || null;
        }
        if (status !== "active") {
          return { error: "Message request pending." };
        }
        try {
          await addMessageDocWithReplyFallback(messagesRef(), {
            user_id: auth.currentUser?.uid,
            message: text,
            message_type: "text",
            created_at: serverTimestamp(),
            updated_at: serverTimestamp(),
            ...(clientId ? { client_id: clientId } : {}),
            ...replyToPayload(replyTo),
          });
          return { error: null };
        } catch (error) {
          if (__DEV__) console.error("DM sendText error:", error);
          return { error };
        }
      },
      // Attachment sends (image/GIF/poll/event share) are active-thread only:
      // a pending request carries just the requester's first text (callable).
      // Message shapes mirror club_messages (message_type 'image' for photos
      // and GIFs, 'poll', 'event_share'); firestore.rules pin each allowlist.
      sendImage: async ({ localUri, text, clientId, replyTo }) => {
        if (getStatus() !== "active") return { error: NOT_ACTIVE_ERROR };
        const uid = auth.currentUser?.uid;
        if (!uid) return { error: "You must be signed in." };
        const uploaded = await UploadService.uploadImageViaREST(
          localUri,
          `conversation-images/${conversationId}/${uid}`,
          `chat-image-${Date.now()}.jpg`
        );
        if (uploaded.error) return { error: uploaded.error };
        return addMessage({
          message: text || "",
          message_type: "image",
          image_url: uploaded.data.url,
          image_path: uploaded.data.path,
          ...(clientId ? { client_id: clientId } : {}),
          ...replyToPayload(replyTo),
        });
      },
      // A GIF is a remote-url image message with no image_path (same as club).
      sendGif: async (imageUrl) => {
        if (getStatus() !== "active") return { error: NOT_ACTIVE_ERROR };
        if (typeof imageUrl !== "string" || !imageUrl.startsWith("https://")) {
          return { error: "Invalid GIF." };
        }
        return addMessage({ message: "", message_type: "image", image_url: imageUrl });
      },
      sendPoll: async ({ question, options }) => {
        if (getStatus() !== "active") return { error: NOT_ACTIVE_ERROR };
        // Mirrors validPollShape()/validPollOptions() (defense in depth).
        const q = typeof question === "string" ? question.trim() : "";
        const opts = Array.isArray(options)
          ? options.map((o) => (typeof o === "string" ? o.trim() : "")).filter(Boolean)
          : [];
        if (!q) return { error: "Poll question cannot be empty" };
        if (q.length > 200) return { error: "Poll question is too long (max 200 characters)" };
        if (opts.length < 2 || opts.length > 6) return { error: "Poll needs 2-6 options" };
        if (opts.some((o) => o.length > 80)) return { error: "Poll option is too long (max 80 characters)" };
        return addMessage({
          message: q,
          message_type: "poll",
          poll: { question: q, options: opts, votes: {} },
        });
      },
      votePoll: async (messageId, optionIndex = null) => {
        if (getStatus() !== "active") return { error: NOT_ACTIVE_ERROR };
        const uid = auth.currentUser?.uid;
        if (!uid) return { error: "You must be signed in." };
        if (!messageId) return { error: "messageId is required" };
        if (optionIndex !== null && (!Number.isInteger(optionIndex) || optionIndex < 0)) {
          return { error: "Invalid poll option" };
        }
        try {
          const messageRef = doc(messagesRef(), messageId);
          const snap = await getDoc(messageRef);
          if (!snap.exists()) return { error: "Poll message not found" };
          const data = snap.data();
          if (data.message_type !== "poll" || !data.poll) return { error: "Message is not a poll" };
          const count = Array.isArray(data.poll.options) ? data.poll.options.length : 0;
          if (optionIndex !== null && optionIndex >= count) return { error: "Invalid poll option" };
          await updateDoc(messageRef, {
            [`poll.votes.${uid}`]: optionIndex === null ? deleteField() : optionIndex,
          });
          return { error: null };
        } catch (error) {
          if (__DEV__) console.error("DM votePoll error:", error);
          return { error };
        }
      },
      // Own-key reaction write (rules: reactionOnlyUpdate, active thread only).
      setMessageReaction: async (messageId, emoji = null) => {
        if (getStatus() !== "active") return { error: NOT_ACTIVE_ERROR };
        const uid = auth.currentUser?.uid;
        if (!uid) return { error: "You must be signed in." };
        if (!messageId) return { error: "messageId is required" };
        if (
          emoji !== null &&
          (typeof emoji !== "string" || emoji.trim().length === 0 || emoji.length > MAX_REACTION_EMOJI_LENGTH)
        ) {
          return { error: "Invalid reaction" };
        }
        try {
          await updateDoc(doc(messagesRef(), messageId), {
            [`message_reactions.${uid}`]: emoji === null ? deleteField() : emoji,
          });
          return { error: null };
        } catch (error) {
          if (__DEV__) console.error("DM setMessageReaction error:", error);
          return { error };
        }
      },
      // Event card (same event_share payload as EventChatScreen's share).
      sendEventShare: async (share) => {
        if (getStatus() !== "active") return { error: NOT_ACTIVE_ERROR };
        if (!share || typeof share.id !== "string" || typeof share.title !== "string") {
          return { error: "Couldn't share this event." };
        }
        return addMessage({
          message: "",
          message_type: "event_share",
          event_share: {
            id: share.id,
            title: share.title.slice(0, 300),
            club_name: share.club_name || null,
            club_logo_url: typeof share.club_logo_url === "string" ? share.club_logo_url : null,
            start_date: typeof share.start_date === "string" ? share.start_date : null,
            location: typeof share.location === "string" ? share.location : null,
            image_url: typeof share.image_url === "string" ? share.image_url : null,
          },
        });
      },
      editMessage: async (messageId, newText) => {
        if (getStatus() !== "active") {
          return { error: "You can edit messages once your request is accepted." };
        }
        try {
          await updateDoc(doc(messagesRef(), messageId), {
            message: newText,
            updated_at: serverTimestamp(),
            edited: true,
          });
          return { error: null };
        } catch (error) {
          if (__DEV__) console.error("DM editMessage error:", error);
          return { error };
        }
      },
      deleteMessage: async (messageId) => {
        // Rules allow deleting only while you can still send (not the request
        // message of a pending request).
        if (getStatus() !== "active") {
          return { error: "You can delete messages once your request is accepted." };
        }
        try {
          const messageRef = doc(messagesRef(), messageId);
          // Remember the uploaded image (if any) so its object can be removed
          // once the message is gone (best-effort; storage.rules allow the
          // uploader to delete their own DM image).
          let imagePath = null;
          try {
            const snap = await getDoc(messageRef);
            const p = snap.exists() ? snap.data().image_path : null;
            if (typeof p === "string" && p.startsWith(`conversation-images/${conversationId}/`)) imagePath = p;
          } catch {
            // Not critical: the delete below decides the outcome.
          }
          await deleteDoc(messageRef);
          if (imagePath) {
            deleteObject(storageRef(storage, imagePath)).catch(() => {});
          }
          return { error: null };
        } catch (error) {
          if (__DEV__) console.error("DM deleteMessage error:", error);
          return { error };
        }
      },
      // Required: without these ChatRoom falls back to club_messages queries.
      getPinnedMessages: async () => ({ data: [], error: null }),
      subscribePinnedMessages: (cb) => {
        cb([]);
        return () => {};
      },
    };
  }
}
